import { useState, useEffect, useRef } from 'react';
import {
  FileText, Upload, Plus, Trash2, Send, ArrowLeft, Loader2,
  MessageSquare, X, Sparkles, HelpCircle, Brain, BarChart3,
  Table2, Volume2, RefreshCw, ChevronLeft, ChevronRight,
  Pause, Play, RotateCcw, Target, Zap, GraduationCap,
  ChevronDown, ChevronUp, Eye, EyeOff, CheckCircle2, XCircle,
  Lightbulb, Trophy, AlertTriangle, Clock, TrendingUp,
  Search, Columns, FileCheck, Star, ArrowUpDown, Shield, Hash
} from 'lucide-react';
import { notebookApi } from '../services/api';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';

// Artifact type configs
const ARTIFACT_TYPES = [
  { type: 'audio_script', label: 'Audio Overview', icon: Volume2, color: 'red', bgColor: 'bg-red-50', textColor: 'text-red-600', borderColor: 'border-red-200' },
  { type: 'mindmap', label: 'Mind Map', icon: Brain, color: 'teal', bgColor: 'bg-teal-50', textColor: 'text-teal-600', borderColor: 'border-teal-200' },
  { type: 'report', label: 'Reports', icon: BarChart3, color: 'green', bgColor: 'bg-green-50', textColor: 'text-green-600', borderColor: 'border-green-200' },
  { type: 'flashcards', label: 'Flashcards', icon: RotateCcw, color: 'purple', bgColor: 'bg-purple-50', textColor: 'text-purple-600', borderColor: 'border-purple-200' },
  { type: 'quiz', label: 'Quiz', icon: HelpCircle, color: 'blue', bgColor: 'bg-blue-50', textColor: 'text-blue-600', borderColor: 'border-blue-200' },
  { type: 'datatable', label: 'Data Table', icon: Table2, color: 'amber', bgColor: 'bg-amber-50', textColor: 'text-amber-600', borderColor: 'border-amber-200' },
  { type: 'formula_forge', label: 'Formula & Shortcuts', icon: Zap, color: 'cyan', bgColor: 'bg-cyan-50', textColor: 'text-cyan-600', borderColor: 'border-cyan-200' },
  { type: 'socratic_drill', label: 'Socratic Drill', icon: GraduationCap, color: 'indigo', bgColor: 'bg-indigo-50', textColor: 'text-indigo-600', borderColor: 'border-indigo-200' },
  { type: 'pyq_analyzer', label: 'PYQ Patterns', icon: Search, color: 'rose', bgColor: 'bg-rose-50', textColor: 'text-rose-600', borderColor: 'border-rose-200' },
  { type: 'comparison_table', label: 'Compare', icon: Columns, color: 'emerald', bgColor: 'bg-emerald-50', textColor: 'text-emerald-600', borderColor: 'border-emerald-200' },
  { type: 'cheat_sheet', label: 'Cheat Sheet', icon: FileCheck, color: 'pink', bgColor: 'bg-pink-50', textColor: 'text-pink-600', borderColor: 'border-pink-200' },
];

function Notebooks() {
  const [notebooks, setNotebooks] = useState([]);
  const [selectedNotebook, setSelectedNotebook] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => { loadNotebooks(); }, []);

  const loadNotebooks = async () => {
    try {
      const res = await notebookApi.list();
      setNotebooks(res.data || []);
    } catch (error) {
      toast.error('Failed to load notebooks');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this notebook and all its content?')) return;
    try {
      await notebookApi.delete(id);
      setNotebooks(prev => prev.filter(n => n.id !== id));
      if (selectedNotebook?.id === id) setSelectedNotebook(null);
      toast.success('Notebook deleted');
    } catch (error) { toast.error('Failed to delete'); }
  };

  if (selectedNotebook) {
    return <NotebookView notebook={selectedNotebook} onBack={() => { setSelectedNotebook(null); loadNotebooks(); }} />;
  }

  if (isLoading) {
    return <div className="flex items-center justify-center h-full"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div></div>;
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">AI Notebooks</h1>
          <p className="text-gray-600 mt-1">Upload study materials and generate AI-powered study tools</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
          <Plus className="w-4 h-4 mr-2" /> New Notebook
        </button>
      </div>

      {showCreate && <CreateNotebook onClose={() => setShowCreate(false)} onCreated={(nb) => { setNotebooks(prev => [nb, ...prev]); setShowCreate(false); setSelectedNotebook(nb); }} />}

      {notebooks.length === 0 ? (
        <div className="text-center py-16">
          <Sparkles className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-500">No notebooks yet</h3>
          <p className="text-gray-400 mt-1">Upload a PDF or paste notes to start</p>
          <button onClick={() => setShowCreate(true)} className="mt-4 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">Create Your First Notebook</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {notebooks.map(nb => (
            <div key={nb.id} className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1 cursor-pointer" onClick={() => setSelectedNotebook(nb)}>
                  <div className="flex items-center mb-2">
                    <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center mr-3">
                      <FileText className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{nb.title}</h3>
                      <p className="text-xs text-gray-500">{nb.file_name ? `${nb.file_type?.toUpperCase()} file` : 'Text notes'}{nb.content_length > 0 && ` - ${Math.round(nb.content_length / 1000)}k chars`}</p>
                    </div>
                  </div>
                </div>
                <button onClick={() => handleDelete(nb.id)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
              </div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                <span className="text-xs text-gray-400">{new Date(nb.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                <button onClick={() => setSelectedNotebook(nb)} className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center">
                  <Sparkles className="w-3.5 h-3.5 mr-1" /> Open Studio
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CreateNotebook({ onClose, onCreated }) {
  const [mode, setMode] = useState('upload');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [file, setFile] = useState(null);
  const [isCreating, setIsCreating] = useState(false);

  const handleCreate = async () => {
    if (mode === 'upload' && !file) { toast.error('Please select a file'); return; }
    if (mode === 'text' && !content.trim()) { toast.error('Please enter some content'); return; }
    setIsCreating(true);
    try {
      const data = mode === 'upload' ? { file, title: title || undefined } : { title: title || 'Untitled Notes', content };
      const res = await notebookApi.create(data);
      onCreated({ id: res.data.id, title: res.data.title, created_at: new Date().toISOString() });
      toast.success('Notebook created!');
    } catch (error) { toast.error(error.response?.data?.error || 'Failed to create notebook'); }
    finally { setIsCreating(false); }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Create Notebook</h3>
        <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
      </div>
      <div className="flex space-x-2 mb-4">
        <button onClick={() => setMode('upload')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${mode === 'upload' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700'}`}><Upload className="w-4 h-4 inline mr-1.5" />Upload File</button>
        <button onClick={() => setMode('text')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${mode === 'text' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700'}`}><FileText className="w-4 h-4 inline mr-1.5" />Paste Text</button>
      </div>
      <div className="space-y-3">
        <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="Notebook title (optional for uploads)" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
        {mode === 'upload' ? (
          <div>
            <input type="file" accept=".pdf,.txt,.md" onChange={e => setFile(e.target.files[0])} className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100" />
            <p className="text-xs text-gray-400 mt-1">Supports PDF, TXT, and MD files (max 10MB)</p>
          </div>
        ) : (
          <textarea value={content} onChange={e => setContent(e.target.value)} rows={8} placeholder="Paste your study notes, chapter text, or any content here..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm" />
        )}
        <button onClick={handleCreate} disabled={isCreating} className="w-full py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors">{isCreating ? 'Creating...' : 'Create Notebook'}</button>
      </div>
    </div>
  );
}

// Main notebook view with toolbar + chat + artifact viewers
function NotebookView({ notebook, onBack }) {
  const [fullNotebook, setFullNotebook] = useState(null);
  const [artifacts, setArtifacts] = useState([]);
  const [activePanel, setActivePanel] = useState('chat'); // 'chat' or artifact type
  const [generating, setGenerating] = useState(null); // which type is being generated
  const [activeArtifact, setActiveArtifact] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Topic selection state
  const [showTopicModal, setShowTopicModal] = useState(false);
  const [topics, setTopics] = useState([]);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [pendingArtifactType, setPendingArtifactType] = useState(null);

  useEffect(() => { loadData(); }, [notebook.id]);

  const loadData = async () => {
    try {
      const [nbRes, artRes] = await Promise.all([
        notebookApi.get(notebook.id),
        notebookApi.getArtifacts(notebook.id)
      ]);
      setFullNotebook(nbRes.data);
      setArtifacts(artRes.data || []);
    } catch (error) { toast.error('Failed to load'); onBack(); }
    finally { setIsLoading(false); }
  };

  const extractTopics = async () => {
    if (topics.length > 0) return; // already extracted
    setLoadingTopics(true);
    try {
      const res = await notebookApi.extractTopics(notebook.id);
      setTopics(res.data.topics || []);
    } catch (error) {
      toast.error('Failed to extract topics');
    } finally { setLoadingTopics(false); }
  };

  const handleGenerate = async (type, topicsToUse) => {
    setShowTopicModal(false);
    setGenerating(type);
    try {
      const res = await notebookApi.generate(notebook.id, type, topicsToUse);
      setActiveArtifact(res.data);
      setActivePanel(type);
      const artRes = await notebookApi.getArtifacts(notebook.id);
      setArtifacts(artRes.data || []);
      toast.success(`${ARTIFACT_TYPES.find(a => a.type === type)?.label} generated!`);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to generate. Try again.');
    } finally { setGenerating(null); }
  };

  const handleViewArtifact = async (type) => {
    const existing = artifacts.find(a => a.type === type);
    if (existing) {
      try {
        const res = await notebookApi.getArtifact(notebook.id, type);
        setActiveArtifact(res.data);
        setActivePanel(type);
      } catch (error) { toast.error('Failed to load'); }
    } else if (type === 'quiz' || type === 'flashcards') {
      // Show topic selection modal only for quiz and flashcards
      setPendingArtifactType(type);
      setSelectedTopics([]);
      setShowTopicModal(true);
      extractTopics();
    } else {
      handleGenerate(type);
    }
  };

  const handleTopicToggle = (topic) => {
    setSelectedTopics(prev => prev.includes(topic) ? prev.filter(t => t !== topic) : [...prev, topic]);
  };

  const handleGenerateWithTopics = () => {
    handleGenerate(pendingArtifactType, selectedTopics.length > 0 ? selectedTopics : undefined);
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-full"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div></div>;
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b bg-white px-4 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center">
          <button onClick={onBack} className="p-1.5 hover:bg-gray-100 rounded-lg mr-2"><ArrowLeft className="w-5 h-5 text-gray-600" /></button>
          <div>
            <h2 className="font-semibold text-gray-900">{notebook.title}</h2>
            <p className="text-xs text-gray-500">{fullNotebook?.file_name || 'Text notes'}{fullNotebook?.content ? ` - ${Math.round(fullNotebook.content.length / 1000)}k chars` : ''}</p>
          </div>
        </div>
      </div>

      {/* Toolbar - NotebookLM style */}
      <div className="bg-gray-50 border-b px-4 py-3 flex-shrink-0">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setActivePanel('chat')}
            className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-all ${activePanel === 'chat' ? 'bg-primary-100 text-primary-700 border border-primary-200' : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300'}`}
          >
            <MessageSquare className="w-4 h-4 mr-1.5" /> Chat
          </button>
          {ARTIFACT_TYPES.map(art => {
            const Icon = art.icon;
            const exists = artifacts.some(a => a.type === art.type);
            const isActive = activePanel === art.type;
            const isGenerating = generating === art.type;
            return (
              <button
                key={art.type}
                onClick={() => handleViewArtifact(art.type)}
                disabled={isGenerating}
                className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  isActive ? `${art.bgColor} ${art.textColor} border ${art.borderColor}` :
                  exists ? `bg-white border border-gray-200 ${art.textColor} hover:${art.borderColor}` :
                  'bg-white border border-gray-200 text-gray-400 hover:text-gray-600 hover:border-gray-300'
                } ${isGenerating ? 'opacity-70' : ''}`}
              >
                {isGenerating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Icon className="w-4 h-4 mr-1.5" />}
                {art.label}
                {exists && !isActive && <span className="ml-1.5 w-1.5 h-1.5 rounded-full bg-green-400"></span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-hidden">
        {activePanel === 'chat' ? (
          <ChatPanel notebookId={notebook.id} notebook={fullNotebook} />
        ) : (
          <ArtifactViewer
            artifact={activeArtifact}
            type={activePanel}
            onRegenerate={() => {
              if (activePanel === 'quiz' || activePanel === 'flashcards') {
                setPendingArtifactType(activePanel);
                setSelectedTopics([]);
                setShowTopicModal(true);
                extractTopics();
              } else {
                handleGenerate(activePanel);
              }
            }}
            isGenerating={generating === activePanel}
          />
        )}
      </div>

      {/* Topic Selection Modal */}
      {showTopicModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[80vh] flex flex-col">
            <div className="px-6 py-4 border-b">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Select Topics</h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Choose topics to focus on, or generate for all topics
                  </p>
                </div>
                <button onClick={() => setShowTopicModal(false)} className="p-1.5 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
            </div>

            <div className="px-6 py-4 flex-1 overflow-y-auto">
              {loadingTopics ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-primary-600 mb-3" />
                  <p className="text-sm text-gray-500">Analyzing document topics...</p>
                </div>
              ) : topics.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-8">Could not extract topics. Will generate for all content.</p>
              ) : (
                <div className="space-y-2">
                  {topics.map((topic, i) => (
                    <label
                      key={i}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedTopics.includes(topic)
                          ? 'border-primary-300 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedTopics.includes(topic)}
                        onChange={() => handleTopicToggle(topic)}
                        className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                      />
                      <span className="text-sm font-medium text-gray-700">{topic}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t bg-gray-50 rounded-b-2xl flex gap-3">
              <button
                onClick={() => setShowTopicModal(false)}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerateWithTopics}
                disabled={loadingTopics}
                className="flex-1 px-4 py-2.5 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
              >
                {selectedTopics.length > 0
                  ? `Generate (${selectedTopics.length} topic${selectedTopics.length > 1 ? 's' : ''})`
                  : 'Generate All Topics'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Chat Panel
function ChatPanel({ notebookId, notebook }) {
  const [chats, setChats] = useState([]);
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { setChats(notebook?.chats || []); }, [notebook]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chats]);

  const handleSend = async () => {
    if (!message.trim() || isSending) return;
    const userMsg = message.trim();
    setMessage('');
    setChats(prev => [...prev, { role: 'user', content: userMsg }]);
    setIsSending(true);
    try {
      const res = await notebookApi.chat(notebookId, userMsg);
      setChats(prev => [...prev, { role: 'assistant', content: res.data.reply }]);
    } catch (error) {
      toast.error('Failed to get AI response');
      setChats(prev => prev.slice(0, -1));
      setMessage(userMsg);
    } finally { setIsSending(false); inputRef.current?.focus(); }
  };

  const suggestedPrompts = ['Summarize this document', 'What are the key points?', 'Explain the main concepts simply', 'What should I focus on for exams?'];

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {chats.length === 0 ? (
          <div className="text-center py-8">
            <MessageSquare className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <h3 className="font-medium text-gray-700">Chat with your document</h3>
            <p className="text-sm text-gray-500 mt-1">Ask questions and AI will answer from the content</p>
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              {suggestedPrompts.map((p, i) => (
                <button key={i} onClick={() => { setMessage(p); inputRef.current?.focus(); }} className="text-xs px-3 py-1.5 bg-purple-50 text-purple-700 rounded-full hover:bg-purple-100">{p}</button>
              ))}
            </div>
          </div>
        ) : chats.map((chat, i) => (
          <div key={i} className={`flex ${chat.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${chat.role === 'user' ? 'bg-primary-600 text-white' : 'bg-white border border-gray-200 text-gray-800'}`}>
              {chat.role === 'assistant' ? <div className="prose prose-sm max-w-none"><ReactMarkdown>{chat.content}</ReactMarkdown></div> : <p className="text-sm whitespace-pre-wrap">{chat.content}</p>}
            </div>
          </div>
        ))}
        {isSending && <div className="flex justify-start"><div className="bg-white border border-gray-200 rounded-2xl px-4 py-3 flex items-center space-x-2"><Loader2 className="w-4 h-4 animate-spin text-purple-500" /><span className="text-sm text-gray-500">Thinking...</span></div></div>}
        <div ref={chatEndRef} />
      </div>
      <div className="border-t bg-white p-4 flex-shrink-0">
        <div className="flex items-end space-x-2">
          <textarea ref={inputRef} value={message} onChange={e => setMessage(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }} placeholder="Ask about your document..." rows={1} className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none text-sm" style={{ minHeight: '42px', maxHeight: '120px' }} />
          <button onClick={handleSend} disabled={!message.trim() || isSending} className="p-2.5 bg-primary-600 text-white rounded-xl hover:bg-primary-700 disabled:opacity-50 transition-colors flex-shrink-0"><Send className="w-5 h-5" /></button>
        </div>
      </div>
    </div>
  );
}

// Artifact Viewer - renders different artifact types
function ArtifactViewer({ artifact, type, onRegenerate, isGenerating }) {
  if (!artifact) return <div className="flex items-center justify-center h-full"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>;

  let parsed = null;
  try {
    if (type !== 'report') parsed = JSON.parse(artifact.content);
  } catch (e) { /* content might not be valid JSON */ }

  return (
    <div className="h-full overflow-y-auto">
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{artifact.title}</h3>
          <button onClick={onRegenerate} disabled={isGenerating} className="flex items-center text-xs px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600">
            {isGenerating ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />} Regenerate
          </button>
        </div>

        {type === 'quiz' && parsed && <QuizViewer questions={parsed} />}
        {type === 'flashcards' && parsed && <FlashcardsViewer cards={parsed} />}
        {type === 'mindmap' && parsed && <MindMapViewer data={parsed} />}
        {type === 'report' && <div className="prose prose-sm max-w-none bg-white border border-gray-200 rounded-xl p-6"><ReactMarkdown>{artifact.content}</ReactMarkdown></div>}
        {type === 'datatable' && parsed && <DataTableViewer tables={parsed} />}
        {type === 'audio_script' && parsed && <AudioViewer script={parsed} />}
        {type === 'exam_strategy' && parsed && <ExamStrategyViewer data={parsed} />}
        {type === 'formula_forge' && parsed && <FormulaForgeViewer data={parsed} />}
        {type === 'socratic_drill' && parsed && <SocraticDrillViewer data={parsed} />}
        {type === 'pyq_analyzer' && parsed && <PYQAnalyzerViewer data={parsed} />}
        {type === 'comparison_table' && parsed && <ComparisonTableViewer data={parsed} />}
        {type === 'cheat_sheet' && parsed && <CheatSheetViewer data={parsed} />}
        {!parsed && type !== 'report' && <div className="bg-white border border-gray-200 rounded-xl p-6 text-sm text-gray-600 whitespace-pre-wrap">{artifact.content}</div>}
      </div>
    </div>
  );
}

// Quiz Viewer
function QuizViewer({ questions }) {
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const score = Object.entries(answers).filter(([i, a]) => questions[i]?.correct === a).length;

  return (
    <div className="space-y-4">
      {questions.map((q, i) => (
        <div key={i} className="bg-white border border-gray-200 rounded-xl p-5">
          <p className="font-medium text-gray-900 mb-3">Q{i + 1}. {q.question}</p>
          <div className="space-y-2">
            {q.options?.map((opt, j) => {
              const letter = opt.charAt(0);
              const isSelected = answers[i] === letter;
              const isCorrect = showResults && q.correct === letter;
              const isWrong = showResults && isSelected && q.correct !== letter;
              return (
                <button key={j} onClick={() => !showResults && setAnswers(prev => ({ ...prev, [i]: letter }))}
                  className={`w-full text-left px-4 py-2.5 rounded-lg border text-sm transition-all ${
                    isCorrect ? 'border-green-500 bg-green-50 text-green-800' :
                    isWrong ? 'border-red-500 bg-red-50 text-red-800' :
                    isSelected ? 'border-primary-500 bg-primary-50 text-primary-800' :
                    'border-gray-200 hover:border-gray-300'
                  }`}>{opt}</button>
              );
            })}
          </div>
          {showResults && q.explanation && <p className="mt-3 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg"><strong>Explanation:</strong> {q.explanation}</p>}
        </div>
      ))}
      <div className="flex items-center justify-between bg-white border border-gray-200 rounded-xl p-4">
        {showResults ? (
          <div className="flex items-center space-x-4">
            <span className="text-lg font-bold text-primary-600">{score}/{questions.length}</span>
            <span className="text-sm text-gray-500">({Math.round(score / questions.length * 100)}% correct)</span>
          </div>
        ) : <span className="text-sm text-gray-500">{Object.keys(answers).length}/{questions.length} answered</span>}
        <button onClick={() => setShowResults(!showResults)} className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700">
          {showResults ? 'Reset' : 'Check Answers'}
        </button>
      </div>
    </div>
  );
}

// Flashcards Viewer
function FlashcardsViewer({ cards }) {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const card = cards[currentIdx];

  return (
    <div className="max-w-lg mx-auto">
      <div onClick={() => setFlipped(!flipped)} className="cursor-pointer bg-white border-2 border-gray-200 rounded-2xl p-8 min-h-[250px] flex flex-col items-center justify-center text-center hover:shadow-lg transition-all">
        <p className="text-xs font-medium text-gray-400 mb-3">{flipped ? 'ANSWER' : 'QUESTION'}</p>
        <p className={`text-lg ${flipped ? 'text-green-700' : 'text-gray-900'} font-medium`}>{flipped ? card?.back : card?.front}</p>
        <p className="text-xs text-gray-400 mt-4">Click to flip</p>
      </div>
      <div className="flex items-center justify-between mt-4">
        <button onClick={() => { setCurrentIdx(Math.max(0, currentIdx - 1)); setFlipped(false); }} disabled={currentIdx === 0} className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-30"><ChevronLeft className="w-5 h-5" /></button>
        <span className="text-sm text-gray-500">{currentIdx + 1} / {cards.length}</span>
        <button onClick={() => { setCurrentIdx(Math.min(cards.length - 1, currentIdx + 1)); setFlipped(false); }} disabled={currentIdx === cards.length - 1} className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-30"><ChevronRight className="w-5 h-5" /></button>
      </div>
    </div>
  );
}

// Mind Map Viewer
function MindMapViewer({ data }) {
  const renderNode = (node, depth = 0) => {
    const colors = ['text-primary-700', 'text-purple-600', 'text-teal-600', 'text-amber-600'];
    const bgColors = ['bg-primary-50', 'bg-purple-50', 'bg-teal-50', 'bg-amber-50'];
    const colorIdx = Math.min(depth, colors.length - 1);
    return (
      <div key={node.title} className={`${depth > 0 ? 'ml-6 border-l-2 border-gray-200 pl-4' : ''}`}>
        <div className={`inline-block px-3 py-1.5 rounded-lg ${bgColors[colorIdx]} ${colors[colorIdx]} font-medium text-sm my-1`}>
          {node.title}
        </div>
        {node.children?.map(child => renderNode(child, depth + 1))}
      </div>
    );
  };
  return <div className="bg-white border border-gray-200 rounded-xl p-6">{renderNode(data)}</div>;
}

// Data Table Viewer
function DataTableViewer({ tables }) {
  return (
    <div className="space-y-6">
      {tables.map((table, i) => (
        <div key={i} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="bg-gray-50 px-4 py-3 border-b"><h4 className="font-semibold text-gray-900 text-sm">{table.title}</h4></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-gray-50">{table.headers?.map((h, j) => <th key={j} className="px-4 py-2.5 text-left font-medium text-gray-700 border-b">{h}</th>)}</tr></thead>
              <tbody>{table.rows?.map((row, j) => <tr key={j} className="border-b last:border-0 hover:bg-gray-50">{row.map((cell, k) => <td key={k} className="px-4 py-2.5 text-gray-600">{cell}</td>)}</tr>)}</tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

// Audio Overview Viewer (with browser TTS)
function AudioViewer({ script }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentLine, setCurrentLine] = useState(-1);
  const utteranceRef = useRef(null);

  const handlePlay = () => {
    if (isPlaying) {
      speechSynthesis.cancel();
      setIsPlaying(false);
      setCurrentLine(-1);
      return;
    }
    setIsPlaying(true);
    let lineIdx = 0;
    const speakNext = () => {
      if (lineIdx >= script.length) { setIsPlaying(false); setCurrentLine(-1); return; }
      setCurrentLine(lineIdx);
      const line = script[lineIdx];
      const utt = new SpeechSynthesisUtterance(line.text);
      utt.rate = 1;
      utt.pitch = line.speaker === 'Host A' ? 1 : 1.2;
      const voices = speechSynthesis.getVoices();
      if (voices.length > 1) utt.voice = line.speaker === 'Host A' ? voices[0] : voices[1];
      utt.onend = () => { lineIdx++; speakNext(); };
      utt.onerror = () => { setIsPlaying(false); setCurrentLine(-1); };
      utteranceRef.current = utt;
      speechSynthesis.speak(utt);
    };
    speakNext();
  };

  useEffect(() => () => speechSynthesis.cancel(), []);

  return (
    <div>
      <div className="flex items-center space-x-3 mb-4">
        <button onClick={handlePlay} className={`flex items-center px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${isPlaying ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-primary-600 text-white hover:bg-primary-700'}`}>
          {isPlaying ? <><Pause className="w-4 h-4 mr-2" />Stop</> : <><Play className="w-4 h-4 mr-2" />Play Audio</>}
        </button>
        <span className="text-xs text-gray-500">Uses browser text-to-speech</span>
      </div>
      <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100 max-h-[500px] overflow-y-auto">
        {script.map((line, i) => (
          <div key={i} className={`px-4 py-3 flex transition-colors ${currentLine === i ? 'bg-primary-50' : ''}`}>
            <span className={`text-xs font-bold w-16 flex-shrink-0 ${line.speaker === 'Host A' ? 'text-primary-600' : 'text-purple-600'}`}>{line.speaker}</span>
            <p className="text-sm text-gray-700 flex-1">{line.text}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// Exam Strategy Viewer
function ExamStrategyViewer({ data }) {
  const [expandedSection, setExpandedSection] = useState('topics');
  const toggle = (s) => setExpandedSection(expandedSection === s ? null : s);

  return (
    <div className="space-y-4">
      {/* Exam Type Badge */}
      <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-orange-100 text-xs font-medium uppercase tracking-wide">Strategy For</p>
            <h3 className="text-xl font-bold mt-1">{data.examType || 'Competitive Exam'}</h3>
          </div>
          <Target className="w-10 h-10 text-orange-200" />
        </div>
      </div>

      {/* Topic Weightage */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <button onClick={() => toggle('topics')} className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50">
          <span className="font-semibold text-gray-900 flex items-center"><TrendingUp className="w-4 h-4 mr-2 text-orange-500" /> Topic Weightage & Priority</span>
          {expandedSection === 'topics' ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </button>
        {expandedSection === 'topics' && (
          <div className="px-5 pb-4 space-y-3">
            {data.topics?.map((t, i) => (
              <div key={i} className="border border-gray-100 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">{t.name}</span>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${t.priority === 'High' ? 'bg-red-100 text-red-700' : t.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}`}>{t.priority} Priority</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">ROI: {t.roi}/10</span>
                  </div>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2.5 mb-2">
                  <div className="bg-orange-500 h-2.5 rounded-full" style={{ width: `${Math.min(t.weightage, 100)}%` }}></div>
                </div>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>Weightage: {t.weightage}%</span>
                  <span>Difficulty: {t.difficulty}</span>
                  <span>Time/Q: {t.timePerQuestion}</span>
                </div>
                {t.tips && <p className="text-xs text-gray-600 mt-2 bg-orange-50 p-2 rounded"><Lightbulb className="w-3 h-3 inline mr-1 text-orange-500" />{t.tips}</p>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Time Strategy */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <button onClick={() => toggle('time')} className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50">
          <span className="font-semibold text-gray-900 flex items-center"><Clock className="w-4 h-4 mr-2 text-blue-500" /> Time Allocation ({data.timeStrategy?.totalTime})</span>
          {expandedSection === 'time' ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </button>
        {expandedSection === 'time' && (
          <div className="px-5 pb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {data.timeStrategy?.sections?.map((s, i) => (
                <div key={i} className="border border-blue-100 bg-blue-50 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900">{s.name}</h4>
                  <div className="mt-2 space-y-1 text-sm">
                    <p className="text-blue-700"><Clock className="w-3 h-3 inline mr-1" />{s.allocatedTime}</p>
                    <p className="text-blue-700">Attempt: {s.questionsToAttempt} questions</p>
                    <p className="text-blue-800 font-medium">Expected: {s.expectedScore}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Quick Wins & Scoring Tips */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h4 className="font-semibold text-gray-900 flex items-center mb-3"><Trophy className="w-4 h-4 mr-2 text-yellow-500" /> Quick Wins</h4>
          <ul className="space-y-2">
            {data.quickWins?.map((w, i) => (
              <li key={i} className="flex items-start text-sm"><CheckCircle2 className="w-4 h-4 mr-2 text-green-500 flex-shrink-0 mt-0.5" /><span className="text-gray-700">{w}</span></li>
            ))}
          </ul>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h4 className="font-semibold text-gray-900 flex items-center mb-3"><AlertTriangle className="w-4 h-4 mr-2 text-red-500" /> Common Mistakes</h4>
          <ul className="space-y-2">
            {data.commonMistakes?.map((m, i) => (
              <li key={i} className="flex items-start text-sm"><XCircle className="w-4 h-4 mr-2 text-red-400 flex-shrink-0 mt-0.5" /><span className="text-gray-700">{m}</span></li>
            ))}
          </ul>
        </div>
      </div>

      {/* Revision Plan */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <button onClick={() => toggle('revision')} className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50">
          <span className="font-semibold text-gray-900 flex items-center"><BarChart3 className="w-4 h-4 mr-2 text-green-500" /> Last Week Revision Plan</span>
          {expandedSection === 'revision' ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </button>
        {expandedSection === 'revision' && (
          <div className="px-5 pb-4">
            <div className="space-y-2">
              {data.revisionPlan?.map((d, i) => (
                <div key={i} className="flex items-center border border-gray-100 rounded-lg p-3">
                  <span className="w-16 text-xs font-bold text-green-600 flex-shrink-0">{d.day}</span>
                  <span className="flex-1 text-sm text-gray-700">{d.focus}</span>
                  <span className="text-xs text-gray-400 flex-shrink-0">{d.hours}h</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Scoring Tips */}
      {data.scoringTips?.length > 0 && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-5">
          <h4 className="font-semibold text-green-900 flex items-center mb-3"><Lightbulb className="w-4 h-4 mr-2" /> Scoring Tips</h4>
          <ul className="space-y-2">
            {data.scoringTips.map((tip, i) => (
              <li key={i} className="text-sm text-green-800 flex items-start"><span className="w-5 h-5 rounded-full bg-green-200 text-green-800 flex items-center justify-center text-xs font-bold mr-2 flex-shrink-0 mt-0.5">{i + 1}</span>{tip}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// Formula & Shortcuts Viewer
function FormulaForgeViewer({ data }) {
  const [expandedFormula, setExpandedFormula] = useState(null);
  const [showMnemonics, setShowMnemonics] = useState(false);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-cyan-100 text-xs font-medium uppercase tracking-wide">Formula & Shortcut Forge</p>
            <h3 className="text-xl font-bold mt-1">{data.formulas?.length || 0} Formulas · {data.speedTricks?.length || 0} Speed Tricks</h3>
          </div>
          <Zap className="w-10 h-10 text-cyan-200" />
        </div>
      </div>

      {/* Formulas */}
      <div className="space-y-3">
        {data.formulas?.map((f, i) => (
          <div key={i} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <button onClick={() => setExpandedFormula(expandedFormula === i ? null : i)} className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50">
              <div className="flex items-center">
                <span className="w-8 h-8 rounded-lg bg-cyan-100 text-cyan-700 flex items-center justify-center text-sm font-bold mr-3">{i + 1}</span>
                <div className="text-left">
                  <p className="font-medium text-gray-900">{f.name}</p>
                  <p className="text-sm text-cyan-600 font-mono">{f.formula}</p>
                </div>
              </div>
              {expandedFormula === i ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </button>
            {expandedFormula === i && (
              <div className="px-5 pb-4 space-y-3 border-t border-gray-100 pt-3">
                <div className="bg-purple-50 border border-purple-100 rounded-lg p-3">
                  <p className="text-xs font-bold text-purple-600 uppercase mb-1">Memory Trick</p>
                  <p className="text-sm text-purple-800">{f.mnemonic}</p>
                </div>
                <div className="bg-cyan-50 border border-cyan-100 rounded-lg p-3">
                  <p className="text-xs font-bold text-cyan-600 uppercase mb-1">Shortcut Method</p>
                  <p className="text-sm text-cyan-800">{f.shortcut}</p>
                </div>
                {f.commonTrap && (
                  <div className="bg-red-50 border border-red-100 rounded-lg p-3">
                    <p className="text-xs font-bold text-red-600 uppercase mb-1">Common Trap</p>
                    <p className="text-sm text-red-800">{f.commonTrap}</p>
                  </div>
                )}
                {f.example && (
                  <div className="bg-green-50 border border-green-100 rounded-lg p-3">
                    <p className="text-xs font-bold text-green-600 uppercase mb-1">Quick Example</p>
                    <p className="text-sm text-green-800">{f.example}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Mnemonic Groups */}
      {data.mnemonicGroups?.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <button onClick={() => setShowMnemonics(!showMnemonics)} className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50">
            <span className="font-semibold text-gray-900 flex items-center"><Brain className="w-4 h-4 mr-2 text-purple-500" /> Master Mnemonics ({data.mnemonicGroups.length})</span>
            {showMnemonics ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </button>
          {showMnemonics && (
            <div className="px-5 pb-4 space-y-3">
              {data.mnemonicGroups.map((g, i) => (
                <div key={i} className="bg-purple-50 border border-purple-100 rounded-lg p-4">
                  <p className="font-medium text-purple-900">{g.topic}</p>
                  <p className="text-sm text-purple-700 mt-1 font-medium">{g.mnemonic}</p>
                  <p className="text-xs text-purple-500 mt-2">Covers: {g.covers?.join(', ')}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Speed Tricks */}
      {data.speedTricks?.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h4 className="font-semibold text-gray-900 flex items-center mb-3"><Zap className="w-4 h-4 mr-2 text-yellow-500" /> Speed Tricks</h4>
          <div className="space-y-3">
            {data.speedTricks.map((t, i) => (
              <div key={i} className="border border-yellow-100 bg-yellow-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-1">
                  <p className="font-medium text-yellow-900">{t.name}</p>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-200 text-yellow-800">Saves {t.timeSaved}</span>
                </div>
                <p className="text-sm text-yellow-800">{t.description}</p>
                <p className="text-xs text-yellow-600 mt-1">Works for: {t.applicableTo}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Verification Methods */}
      {data.verificationMethods?.length > 0 && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-5">
          <h4 className="font-semibold text-green-900 flex items-center mb-3"><CheckCircle2 className="w-4 h-4 mr-2" /> Quick Verification Methods</h4>
          <div className="space-y-2">
            {data.verificationMethods.map((v, i) => (
              <div key={i} className="flex items-start">
                <span className="w-5 h-5 rounded-full bg-green-200 text-green-800 flex items-center justify-center text-xs font-bold mr-2 flex-shrink-0 mt-0.5">{i + 1}</span>
                <div>
                  <p className="text-sm font-medium text-green-900">{v.name}</p>
                  <p className="text-xs text-green-700">{v.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Socratic Drill Viewer
function SocraticDrillViewer({ data }) {
  const [currentQ, setCurrentQ] = useState(0);
  const [showHint, setShowHint] = useState(0); // 0 = no hint, 1-3 = hint level
  const [showAnswer, setShowAnswer] = useState(false);
  const [userAnswer, setUserAnswer] = useState('');
  const [results, setResults] = useState({}); // { idx: 'correct'|'wrong' }
  const [drillComplete, setDrillComplete] = useState(false);

  const questions = data.questions || [];
  const q = questions[currentQ];
  const correctCount = Object.values(results).filter(r => r === 'correct').length;
  const wrongCount = Object.values(results).filter(r => r === 'wrong').length;

  const handleMark = (result) => {
    setResults(prev => ({ ...prev, [currentQ]: result }));
    if (currentQ < questions.length - 1) {
      setTimeout(() => {
        setCurrentQ(currentQ + 1);
        setShowHint(0);
        setShowAnswer(false);
        setUserAnswer('');
      }, 500);
    } else {
      setDrillComplete(true);
    }
  };

  const difficultyColor = (d) => {
    if (d?.includes('Basic')) return 'bg-green-100 text-green-700';
    if (d?.includes('Application') || d?.includes('Understanding')) return 'bg-yellow-100 text-yellow-700';
    return 'bg-red-100 text-red-700';
  };

  if (drillComplete) {
    const pct = Math.round((correctCount / questions.length) * 100);
    const level = correctCount <= 3 ? 'levels_1_3' : correctCount <= 6 ? 'levels_4_6' : 'levels_7_10';
    return (
      <div className="max-w-lg mx-auto space-y-4">
        <div className={`rounded-2xl p-8 text-center ${pct >= 70 ? 'bg-gradient-to-br from-green-400 to-emerald-500' : pct >= 40 ? 'bg-gradient-to-br from-yellow-400 to-orange-500' : 'bg-gradient-to-br from-red-400 to-pink-500'} text-white`}>
          <Trophy className="w-16 h-16 mx-auto mb-3 opacity-90" />
          <h3 className="text-3xl font-bold">{correctCount}/{questions.length}</h3>
          <p className="text-lg mt-1 opacity-90">{pct >= 70 ? 'Excellent Mastery!' : pct >= 40 ? 'Good Progress!' : 'Keep Practicing!'}</p>
          <p className="text-sm mt-2 opacity-75">{data.masteryThreshold}</p>
        </div>
        {/* Weak area advice */}
        {data.weakAreaAdvice?.[level] && (
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h4 className="font-semibold text-gray-900 flex items-center mb-2"><Lightbulb className="w-4 h-4 mr-2 text-yellow-500" /> Improvement Advice</h4>
            <p className="text-sm text-gray-700">{data.weakAreaAdvice[level]}</p>
          </div>
        )}
        {/* Per-question results */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h4 className="font-semibold text-gray-900 mb-3">Question Breakdown</h4>
          <div className="space-y-2">
            {questions.map((qq, i) => (
              <div key={i} className="flex items-center text-sm">
                {results[i] === 'correct' ? <CheckCircle2 className="w-4 h-4 text-green-500 mr-2" /> : <XCircle className="w-4 h-4 text-red-500 mr-2" />}
                <span className="text-gray-600 flex-1">L{qq.level}: {qq.question.substring(0, 60)}...</span>
                {results[i] === 'wrong' && qq.knowledge_gap && <span className="text-xs text-red-500">{qq.knowledge_gap}</span>}
              </div>
            ))}
          </div>
        </div>
        <button onClick={() => { setCurrentQ(0); setResults({}); setDrillComplete(false); setShowHint(0); setShowAnswer(false); setUserAnswer(''); }} className="w-full py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 font-medium">
          Retry Drill
        </button>
      </div>
    );
  }

  if (!q) return <div className="text-center text-gray-500 py-8">No questions available</div>;

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      {/* Progress bar */}
      <div className="flex items-center gap-3">
        <div className="flex-1 bg-gray-100 rounded-full h-2">
          <div className="bg-indigo-500 h-2 rounded-full transition-all" style={{ width: `${((currentQ) / questions.length) * 100}%` }}></div>
        </div>
        <span className="text-xs text-gray-500">{currentQ + 1}/{questions.length}</span>
        <span className="text-xs text-green-600">{correctCount} correct</span>
        <span className="text-xs text-red-500">{wrongCount} wrong</span>
      </div>

      {/* Question Card */}
      <div className="bg-white border-2 border-indigo-200 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-bold">L{q.level}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${difficultyColor(q.difficulty)}`}>{q.difficulty}</span>
          </div>
          <GraduationCap className="w-5 h-5 text-indigo-400" />
        </div>

        <p className="text-lg font-medium text-gray-900 mb-4">{q.question}</p>

        {/* User's thinking space */}
        <textarea
          value={userAnswer}
          onChange={e => setUserAnswer(e.target.value)}
          placeholder="Type your thinking/answer here (for your reference)..."
          rows={3}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none mb-4"
        />

        {/* Hints */}
        <div className="space-y-2 mb-4">
          {q.hints?.slice(0, showHint).map((hint, i) => (
            <div key={i} className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800 flex items-start">
              <Lightbulb className="w-4 h-4 mr-2 text-yellow-500 flex-shrink-0 mt-0.5" />
              <span><strong>Hint {i + 1}:</strong> {hint}</span>
            </div>
          ))}
          {showHint < (q.hints?.length || 0) && !showAnswer && (
            <button onClick={() => setShowHint(showHint + 1)} className="text-sm text-yellow-600 hover:text-yellow-700 flex items-center">
              <Eye className="w-4 h-4 mr-1" /> Show Hint {showHint + 1}
            </button>
          )}
        </div>

        {/* Answer */}
        {showAnswer ? (
          <div className="space-y-3">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-xs font-bold text-green-600 uppercase mb-1">Answer</p>
              <p className="text-sm text-green-800 font-medium">{q.answer}</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-xs font-bold text-blue-600 uppercase mb-1">Explanation</p>
              <p className="text-sm text-blue-800">{q.explanation}</p>
            </div>
            {q.followUp && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <p className="text-xs font-bold text-purple-600 uppercase mb-1">Think Deeper</p>
                <p className="text-sm text-purple-800">{q.followUp}</p>
              </div>
            )}
            {/* Self-assess buttons */}
            <div className="flex gap-3">
              <button onClick={() => handleMark('correct')} className="flex-1 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 font-medium flex items-center justify-center">
                <CheckCircle2 className="w-4 h-4 mr-2" /> I Got It Right
              </button>
              <button onClick={() => handleMark('wrong')} className="flex-1 py-3 bg-red-500 text-white rounded-xl hover:bg-red-600 font-medium flex items-center justify-center">
                <XCircle className="w-4 h-4 mr-2" /> I Got It Wrong
              </button>
            </div>
          </div>
        ) : (
          <button onClick={() => setShowAnswer(true)} className="w-full py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 font-medium flex items-center justify-center">
            <EyeOff className="w-4 h-4 mr-2" /> Reveal Answer
          </button>
        )}
      </div>
    </div>
  );
}

// PYQ Pattern Analyzer Viewer
function PYQAnalyzerViewer({ data }) {
  const [expandedQ, setExpandedQ] = useState(null);
  const freqColor = { 'Very High': 'bg-red-100 text-red-700', 'High': 'bg-orange-100 text-orange-700', 'Medium': 'bg-yellow-100 text-yellow-700', 'Low': 'bg-gray-100 text-gray-600' };
  const trendIcon = { 'Increasing': '📈', 'Stable': '➡️', 'Decreasing': '📉' };
  const probColor = { 'Very Likely': 'bg-red-500', 'Likely': 'bg-orange-500', 'Possible': 'bg-yellow-500' };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-r from-rose-500 to-pink-600 rounded-xl p-5 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold flex items-center"><Search className="w-5 h-5 mr-2" /> PYQ Pattern Analysis</h3>
            <p className="text-rose-100 text-sm mt-1">Exam: {data.examType}</p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold">{data.topicAnalysis?.length || 0}</div>
            <div className="text-xs text-rose-200">Topics Analyzed</div>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <span className="px-2 py-0.5 bg-white/20 rounded text-xs">Overall Importance: {data.overallImportance}</span>
        </div>
      </div>

      {/* Topic Analysis */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b font-semibold text-sm text-gray-700 flex items-center"><TrendingUp className="w-4 h-4 mr-2" /> Topic-wise Frequency Analysis</div>
        <div className="divide-y">
          {(data.topicAnalysis || []).map((t, i) => (
            <div key={i} className="p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-gray-900">{t.topic}</h4>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${freqColor[t.frequency] || 'bg-gray-100 text-gray-600'}`}>{t.frequency}</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <div className="bg-gray-50 rounded-lg p-2 text-center">
                  <div className="text-gray-500">Trend</div>
                  <div className="font-medium">{trendIcon[t.yearlyTrend]} {t.yearlyTrend}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-2 text-center">
                  <div className="text-gray-500">Avg Qs/Year</div>
                  <div className="font-bold text-lg text-rose-600">{t.avgQuestionsPerYear}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-2 text-center">
                  <div className="text-gray-500">Weightage</div>
                  <div className="font-bold text-lg text-blue-600">{t.weightagePercent}%</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-2 text-center">
                  <div className="text-gray-500">Difficulty</div>
                  <div className="font-medium">{t.difficultyRange}</div>
                </div>
              </div>
              {t.lastAppeared && <p className="text-xs text-gray-400 mt-2">Last appeared: {t.lastAppeared}</p>}
            </div>
          ))}
        </div>
      </div>

      {/* Repeated Patterns */}
      {data.repeatedPatterns?.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b font-semibold text-sm text-gray-700 flex items-center"><RotateCcw className="w-4 h-4 mr-2" /> Repeated Patterns</div>
          <div className="p-4 space-y-3">
            {data.repeatedPatterns.map((p, i) => (
              <div key={i} className="border border-rose-100 rounded-lg p-3 bg-rose-50/50">
                <div className="flex items-start justify-between">
                  <p className="text-sm font-medium text-gray-900">{p.pattern}</p>
                  <span className="px-2 py-0.5 bg-rose-100 text-rose-700 rounded text-xs flex-shrink-0 ml-2">{p.frequency}</span>
                </div>
                <div className="mt-2 bg-white rounded p-2 text-xs text-gray-600 border"><strong>Example:</strong> {p.exampleQuestion}</div>
                <p className="mt-1 text-xs text-rose-600 flex items-center"><Lightbulb className="w-3 h-3 mr-1" /> {p.tip}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Predicted Questions */}
      {data.predictedQuestions?.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b font-semibold text-sm text-gray-700 flex items-center"><Target className="w-4 h-4 mr-2" /> Predicted Questions</div>
          <div className="divide-y">
            {data.predictedQuestions.map((q, i) => (
              <div key={i} className="p-4 cursor-pointer hover:bg-gray-50" onClick={() => setExpandedQ(expandedQ === i ? null : i)}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold text-gray-400">Q{i + 1}</span>
                      <span className={`w-2 h-2 rounded-full ${probColor[q.probability] || 'bg-gray-400'}`} />
                      <span className="text-xs text-gray-500">{q.probability}</span>
                      <span className="text-xs px-1.5 py-0.5 bg-gray-100 rounded">{q.type}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${q.difficulty === 'Hard' ? 'bg-red-100 text-red-600' : q.difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-600' : 'bg-green-100 text-green-600'}`}>{q.difficulty}</span>
                    </div>
                    <p className="text-sm text-gray-900 font-medium">{q.question}</p>
                  </div>
                  {expandedQ === i ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                </div>
                {expandedQ === i && (
                  <div className="mt-3 space-y-2">
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-xs"><strong className="text-yellow-700">Hint:</strong> <span className="text-yellow-800">{q.hint}</span></div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-xs"><strong className="text-green-700">Answer/Approach:</strong> <span className="text-green-800">{q.answer}</span></div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* High-Yield vs Low-Yield */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {data.highYieldTopics?.length > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <h4 className="font-semibold text-green-700 text-sm mb-2 flex items-center"><Star className="w-4 h-4 mr-1" /> High-Yield Topics</h4>
            <ul className="space-y-1">{data.highYieldTopics.map((t, i) => <li key={i} className="text-xs text-green-800 flex items-start"><CheckCircle2 className="w-3 h-3 mr-1 mt-0.5 flex-shrink-0" /> {t}</li>)}</ul>
          </div>
        )}
        {data.lowYieldTopics?.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <h4 className="font-semibold text-red-700 text-sm mb-2 flex items-center"><AlertTriangle className="w-4 h-4 mr-1" /> Low-Yield (Skip or Do Last)</h4>
            <ul className="space-y-1">{data.lowYieldTopics.map((t, i) => <li key={i} className="text-xs text-red-800 flex items-start"><XCircle className="w-3 h-3 mr-1 mt-0.5 flex-shrink-0" /> {t}</li>)}</ul>
          </div>
        )}
      </div>

      {/* Exam Tips */}
      {data.examTips?.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <h4 className="font-semibold text-blue-700 text-sm mb-2 flex items-center"><Lightbulb className="w-4 h-4 mr-1" /> Exam Tips</h4>
          <ul className="space-y-1">{data.examTips.map((t, i) => <li key={i} className="text-xs text-blue-800 flex items-start"><span className="font-bold mr-1">{i + 1}.</span> {t}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

// Comparison Table Viewer
function ComparisonTableViewer({ data }) {
  const relevanceColor = { 'High': 'bg-red-100 text-red-700', 'Medium': 'bg-yellow-100 text-yellow-700', 'Low': 'bg-gray-100 text-gray-600' };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl p-5 text-white">
        <h3 className="text-lg font-bold flex items-center"><Columns className="w-5 h-5 mr-2" /> Comparison Tables</h3>
        <p className="text-emerald-100 text-sm mt-1">{data.tables?.length || 0} comparisons generated from your content</p>
      </div>

      {/* Tables */}
      {(data.tables || []).map((table, i) => (
        <div key={i} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b flex items-center justify-between">
            <div>
              <h4 className="font-semibold text-gray-900 text-sm">{table.title}</h4>
              {table.description && <p className="text-xs text-gray-500 mt-0.5">{table.description}</p>}
            </div>
            {table.examRelevance && <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${relevanceColor[table.examRelevance] || 'bg-gray-100'}`}>Exam: {table.examRelevance}</span>}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-emerald-50">
                  {(table.headers || []).map((h, j) => (
                    <th key={j} className={`px-4 py-2.5 text-left font-semibold text-emerald-800 border-b border-emerald-100 ${j === 0 ? 'w-1/4' : ''}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(table.rows || []).map((row, j) => (
                  <tr key={j} className={j % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    {row.map((cell, k) => (
                      <td key={k} className={`px-4 py-2.5 border-b border-gray-100 ${k === 0 ? 'font-medium text-gray-700' : 'text-gray-600'}`}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Memory trick & exam alert */}
          <div className="p-3 space-y-2">
            {table.memoryTrick && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-2.5 text-xs flex items-start">
                <Lightbulb className="w-4 h-4 text-purple-500 mr-2 flex-shrink-0 mt-0.5" />
                <div><strong className="text-purple-700">Memory Trick:</strong> <span className="text-purple-800">{table.memoryTrick}</span></div>
              </div>
            )}
            {table.examAlert && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-2.5 text-xs flex items-start">
                <AlertTriangle className="w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                <div><strong className="text-red-700">Exam Alert:</strong> <span className="text-red-800">{table.examAlert}</span></div>
              </div>
            )}
          </div>
        </div>
      ))}

      {/* Quick Differences */}
      {data.quickDifferences?.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b font-semibold text-sm text-gray-700 flex items-center"><Zap className="w-4 h-4 mr-2" /> Quick One-Liner Differences</div>
          <div className="p-4 space-y-2">
            {data.quickDifferences.map((d, i) => (
              <div key={i} className="flex items-start bg-emerald-50 rounded-lg p-3">
                <Hash className="w-4 h-4 text-emerald-500 mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-xs font-bold text-emerald-700">{d.pair}: </span>
                  <span className="text-xs text-emerald-800">{d.oneLiner}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Confusion Warnings */}
      {data.confusionWarnings?.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b font-semibold text-sm text-gray-700 flex items-center"><Shield className="w-4 h-4 mr-2" /> Common Confusion Points</div>
          <div className="p-4 space-y-3">
            {data.confusionWarnings.map((w, i) => (
              <div key={i} className="border border-orange-200 rounded-lg p-3 bg-orange-50/50">
                <p className="text-sm font-semibold text-orange-800">{w.topic}</p>
                <p className="text-xs text-orange-600 mt-1"><strong>Why confusing:</strong> {w.warning}</p>
                <p className="text-xs text-green-700 mt-1 bg-green-50 rounded p-2"><strong>Clarification:</strong> {w.clarification}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Cheat Sheet Viewer
function CheatSheetViewer({ data }) {
  const typeIcon = { formulas: '📐', definitions: '📖', keyPoints: '🎯', mnemonics: '🧠', warnings: '⚠️' };
  const typeColor = { formulas: 'border-blue-200 bg-blue-50', definitions: 'border-purple-200 bg-purple-50', keyPoints: 'border-green-200 bg-green-50', mnemonics: 'border-yellow-200 bg-yellow-50', warnings: 'border-red-200 bg-red-50' };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-r from-pink-500 to-rose-600 rounded-xl p-5 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold flex items-center"><FileCheck className="w-5 h-5 mr-2" /> {data.title || 'Exam Cheat Sheet'}</h3>
            <p className="text-pink-100 text-sm mt-1">{data.subject} • Last-minute revision sheet</p>
          </div>
          <div className="bg-white/20 rounded-lg px-3 py-1.5 text-xs">
            <Clock className="w-3 h-3 inline mr-1" /> 30-min revision
          </div>
        </div>
      </div>

      {/* Sections */}
      {(data.sections || []).map((section, i) => (
        <div key={i} className={`border rounded-xl overflow-hidden ${typeColor[section.type] || 'border-gray-200 bg-white'}`}>
          <div className="px-4 py-2.5 border-b border-inherit font-semibold text-sm flex items-center">
            <span className="mr-2">{typeIcon[section.type] || '📌'}</span> {section.heading}
          </div>
          <div className="p-3 space-y-1.5">
            {(section.items || []).map((item, j) => (
              <div key={j} className={`flex items-start text-xs rounded-lg p-2 ${item.highlight ? 'bg-white border border-yellow-300 shadow-sm' : 'bg-white/60'}`}>
                {item.highlight && <Star className="w-3 h-3 text-yellow-500 mr-1.5 flex-shrink-0 mt-0.5" />}
                <span className="font-bold text-gray-700 mr-2 flex-shrink-0">{item.label}:</span>
                <span className="text-gray-800">{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Quick Formulas */}
      {data.quickFormulas?.length > 0 && (
        <div className="bg-white border border-blue-200 rounded-xl overflow-hidden">
          <div className="px-4 py-2.5 bg-blue-50 border-b border-blue-200 font-semibold text-sm text-blue-700 flex items-center">📐 Quick Formulas</div>
          <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {data.quickFormulas.map((f, i) => (
              <div key={i} className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                <p className="text-xs font-bold text-blue-700">{f.name}</p>
                <p className="text-sm font-mono text-blue-900 mt-1 bg-white rounded px-2 py-1 border">{f.formula}</p>
                <p className="text-xs text-blue-500 mt-1">Use when: {f.when}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Must Remember */}
      {data.mustRemember?.length > 0 && (
        <div className="bg-yellow-50 border-2 border-yellow-400 rounded-xl p-4">
          <h4 className="font-bold text-yellow-800 text-sm mb-2 flex items-center"><Star className="w-4 h-4 mr-1 text-yellow-600" /> MUST REMEMBER</h4>
          <ul className="space-y-1.5">
            {data.mustRemember.map((item, i) => (
              <li key={i} className="text-xs text-yellow-900 flex items-start bg-yellow-100/50 rounded-lg p-2">
                <span className="font-bold text-yellow-700 mr-2">{i + 1}.</span> {item}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Common Traps */}
      {data.commonTraps?.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl overflow-hidden">
          <div className="px-4 py-2.5 bg-red-100 border-b border-red-200 font-semibold text-sm text-red-700 flex items-center"><AlertTriangle className="w-4 h-4 mr-1" /> Exam Traps to Avoid</div>
          <div className="p-3 space-y-2">
            {data.commonTraps.map((t, i) => (
              <div key={i} className="bg-white border border-red-100 rounded-lg p-3">
                <p className="text-xs text-red-700 flex items-start"><XCircle className="w-3 h-3 mr-1.5 flex-shrink-0 mt-0.5" /> <strong>Trap:</strong>&nbsp;{t.trap}</p>
                <p className="text-xs text-green-700 mt-1 flex items-start"><CheckCircle2 className="w-3 h-3 mr-1.5 flex-shrink-0 mt-0.5" /> <strong>Avoid:</strong>&nbsp;{t.avoid}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Last Minute Tips */}
      {data.lastMinuteTips?.length > 0 && (
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-4">
          <h4 className="font-bold text-indigo-700 text-sm mb-2 flex items-center"><Clock className="w-4 h-4 mr-1" /> Last Minute Tips</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {data.lastMinuteTips.map((tip, i) => (
              <div key={i} className="bg-white rounded-lg p-2.5 text-xs text-indigo-800 border border-indigo-100 flex items-start">
                <CheckCircle2 className="w-3 h-3 text-indigo-500 mr-1.5 flex-shrink-0 mt-0.5" /> {tip}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default Notebooks;
